import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { jobsAPI, applicationsAPI, applicationsExternalAPI } from '../services/api';
import { Job } from '../types';
import Loading from '../components/Loading';
import ShareComposerModal from '../components/ShareComposerModal';
import { logJobView } from '../utils/activityLog';
import { jobSlug, parseJobIdFromSlug } from '../utils/slug';
import { SEO } from '../components/seo/SEO';
import { buildJobPostingJsonLd } from '../utils/jsonLd';
import { formatJobExperience, hasExperienceRange } from '../utils/experienceRange';
import JobMatchBar from '../components/JobMatchBar';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' ? `${window.location.origin}/api` : '');

const JobDetail: React.FC = () => {
  // The route param is now :slug, but we still accept the legacy numeric ID form for
  // backward compatibility. parseJobIdFromSlug handles both shapes.
  const { slug: slugParam, id: idParam } = useParams<{ slug?: string; id?: string }>();
  const slugOrId = slugParam ?? idParam;
  const id = String(parseJobIdFromSlug(slugOrId) ?? '');
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [coverLetterFile, setCoverLetterFile] = useState<File | null>(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applySuccess, setApplySuccess] = useState<string | null>(null);
  const [resumePrompt, setResumePrompt] = useState<string | null>(null);
  const [showShareComposer, setShowShareComposer] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [relatedJobs, setRelatedJobs] = useState<Job[]>([]);

  const { isAuthenticated, isCandidate, user } = useAuth();

  const fetchJob = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      setLoading(true);
      setHasApplied(false);
      const response = await jobsAPI.getJob(parseInt(id, 10));
      setJob(response.job);
    } catch (error) {
      console.error('Failed to fetch job:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const checkExistingApplication = useCallback(async (jobId: number) => {
    if (!isAuthenticated || !isCandidate) {
      setHasApplied(false);
      return;
    }
    try {
      const data = await applicationsAPI.getApplications({ job_id: jobId, per_page: 1 });
      const alreadyApplied = (data.applications || data.items || []).some(app => app.job_id === jobId);
      setHasApplied(alreadyApplied);
    } catch (error) {
      console.error('Failed to verify application status:', error);
      setHasApplied(false);
    }
  }, [isAuthenticated, isCandidate]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  // Log a candidate's job view for the activity heatmap. Deduped per (day, jobId).
  useEffect(() => {
    if (!job || !isCandidate) return;
    logJobView(job.id);
  }, [job, isCandidate]);

  // Fetch related jobs (same company first, fall back to same location). Up to 6.
  useEffect(() => {
    if (!job) {
      setRelatedJobs([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const buckets: Job[] = [];
        const seen = new Set<number>([job.id]);
        if (job.company_id) {
          const resp = await jobsAPI.getJobs({ company_id: job.company_id, per_page: 10 });
          for (const r of resp.jobs || []) {
            if (!seen.has(r.id) && r.is_active) {
              buckets.push(r);
              seen.add(r.id);
              if (buckets.length >= 4) break;
            }
          }
        }
        if (buckets.length < 6 && job.location) {
          const resp = await jobsAPI.getJobs({ location: job.location, per_page: 10 });
          for (const r of resp.jobs || []) {
            if (!seen.has(r.id) && r.is_active) {
              buckets.push(r);
              seen.add(r.id);
              if (buckets.length >= 6) break;
            }
          }
        }
        if (!cancelled) setRelatedJobs(buckets);
      } catch {
        if (!cancelled) setRelatedJobs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [job]);

  // Canonical URL redirect: if the user lands on an old/incorrect slug, swap the URL
  // to the canonical form without re-fetching. Uses replace so the bad URL doesn't
  // pile up in browser history.
  useEffect(() => {
    if (!job || !slugOrId) return;
    const canonical = jobSlug(job);
    if (slugOrId !== canonical) {
      navigate(`/jobs/${canonical}`, { replace: true });
    }
  }, [job, slugOrId, navigate]);

  const handleApply = async () => {
    if (!job) return;
    if (!user?.resume_filename || !user?.resume_path) {
      setApplyError('Please upload your resume before submitting an application.');
      setResumePrompt('Please upload your resume to apply for this position.');
      setShowApplicationForm(false);
      return;
    }

    setApplying(true);
    setApplyError(null);
    setApplySuccess(null);
    try {
      await applicationsAPI.createApplication({
        job_id: job.id,
        cover_letter: coverLetter || undefined,
        cover_letter_file: coverLetterFile || undefined
      });
      
      setHasApplied(true);
      setShowApplicationForm(false);
      setCoverLetter('');
      setCoverLetterFile(null);
      setApplySuccess('Application submitted successfully!');
      await checkExistingApplication(job.id);
    } catch (error: any) {
      console.error('Failed to apply:', error);
      const message = error?.response?.data?.error || 'Failed to submit application.';
      setApplyError(message);
    } finally {
      setApplying(false);
    }
  };

  const handleStartApplication = () => {
    setApplyError(null);
    setApplySuccess(null);
    if (!user?.resume_filename || !user?.resume_path) {
      setResumePrompt('Please upload your resume to apply for this position.');
      return;
    }
    setResumePrompt(null);
    setShowApplicationForm(true);
  };

  const handleExternalApply = async () => {
    if (!job) return;
    setApplyError(null);
    const target = job.source_url || '';
    if (!target) {
      setApplyError('The source URL for this job is missing. Please try again later.');
      return;
    }
    // Open a tab synchronously so popup blockers treat it as a user action,
    // then update its location once the redirect endpoint resolves.
    const newTab = typeof window !== 'undefined' ? window.open('', '_blank') : null;
    try {
      if (user) {
        const data = await applicationsExternalAPI.redirect(job.id);
        const url = data?.source_url || target;
        if (newTab) {
          newTab.location.href = url;
        } else {
          window.location.href = url;
        }
      } else {
        if (newTab) {
          newTab.location.href = target;
        } else {
          window.location.href = target;
        }
      }
    } catch (err: any) {
      if (newTab) {
        newTab.close();
      }
      setApplyError(err?.response?.data?.error || err?.message || 'Failed to record the redirect. Try again.');
    }
  };

  const isExternal = (job as any)?.source_type === 'external';

  const handleCoverLetterFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const allowedExtensions = ['.pdf', '.doc', '.docx'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setApplyError('Please upload a PDF, DOC, or DOCX file.');
      return;
    }
    if (file.size > 16 * 1024 * 1024) {
      setApplyError('Cover letter file must be less than 16MB.');
      return;
    }
    setCoverLetterFile(file);
    setApplyError(null);
  };

  useEffect(() => {
    if (job) {
      checkExistingApplication(job.id);
    }
  }, [job, checkExistingApplication]);

  useEffect(() => {
    const checkSavedStatus = async () => {
      if (!job || !isAuthenticated || !isCandidate) {
        setIsSaved(false);
        return;
      }
      try {
        let page = 1;
        let hasNext = true;
        let found = false;
        while (hasNext && page <= 20 && !found) {
          const data = await jobsAPI.getSavedJobs({ page, per_page: 100 });
          found = (data.saved_jobs || []).some((item) => item.job_id === job.id);
          hasNext = data.has_next;
          page += 1;
        }
        setIsSaved(found);
      } catch {
        setIsSaved(false);
      }
    };
    checkSavedStatus();
  }, [job, isAuthenticated, isCandidate]);

  const handleToggleSave = async () => {
    if (!job || !isAuthenticated || !isCandidate) return;
    try {
      setSaveLoading(true);
      if (isSaved) {
        await jobsAPI.unsaveJob(job.id);
        setIsSaved(false);
      } else {
        await jobsAPI.saveJob(job.id);
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Failed to update saved job status', error);
    } finally {
      setSaveLoading(false);
    }
  };

  const formatSalary = (min?: number, max?: number, currency: string = 'INR') => {
    if (!min && !max) return 'CTC not disclosed';
    
    const format = (amount: number) => {
      if (currency === 'INR') {
        return `₹${(amount / 100000).toFixed(1)}L`;
      }
      return `${currency} ${amount.toLocaleString()}`;
    };

    if (min && max) {
      return `${format(min)} - ${format(max)}`;
    }
    
    return min ? `${format(min)}+` : `Up to ${format(max!)}`;
  };

  const getLinkedInShareUrl = () => {
    if (!job || typeof window === 'undefined') return '#';
    return `${API_BASE}/linkedin/authorize?job_id=${job.id}`;
  };

  const getJobShareUrl = () => {
    if (!job || typeof window === 'undefined') return '';
    return `${window.location.origin}/jobs/${job.id}`;
  };

  const metaParts = [
    isAuthenticated ? job?.company?.name : null,
    job?.location || 'Remote',
    job?.job_type?.replace('_', ' ')
  ].filter(Boolean) as string[];

  if (loading) {
    return <Loading fullScreen text="Loading job details..." />;
  }

  if (!job) {
    return (
      <div className="container center">
        <div className="card">
          <h1 className="h1">Job Not Found</h1>
          <p className="muted">The job you're looking for doesn't exist or has been removed.</p>
          <Link to="/jobs" className="btn">Browse Other Jobs</Link>
        </div>
      </div>
    );
  }

  const publicBase = (process.env.REACT_APP_PUBLIC_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://talixjobs.com')).replace(/\/$/, '');
  const canonicalUrl = `${publicBase}/jobs/${jobSlug(job)}`;
  const seoDescription = (job.description || '')
    .replace(/<\/?[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160);
  const seoTitle = `${job.title}${job.company?.name ? ` at ${job.company.name}` : ''}${job.location ? ` in ${job.location}` : ''}`;

  return (
    <div className="container">
      <SEO
        title={seoTitle}
        description={seoDescription || `Apply for ${job.title} at ${job.company?.name ?? 'top companies'} on talixjobs.`}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={job.company?.logo_url ?? undefined}
        jsonLd={buildJobPostingJsonLd(job, canonicalUrl)}
      />
      <div className="grid grid-3 job-detail-layout">
        <article className="card shadow job-detail-main">
          <div className="job-detail-section">
            <h1 className="h1">{job.title}</h1>
            <div className="job-detail-meta">
              <p className="muted">{metaParts.join(' • ')}</p>
              {job.remote_allowed && (
                <span className="badge green">Remote Applications Accepted</span>
              )}
            </div>
            <div className="job-detail-salary">
              {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
            </div>
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>Your match:</div>
              <JobMatchBar
                jobId={job.id}
                initialScore={job.my_match?.score ?? null}
                initialBracket={job.my_match?.bracket}
                size="md"
                loggedOutMode="cta"
              />
            </div>
          </div>

          <div className="job-detail-section">
            <h2 className="h2">About the role</h2>
            <div className="job-detail-text">
              {job.description}
            </div>
          </div>

          {job.requirements && (
            <div className="job-detail-section">
              <h2 className="h2">Requirements</h2>
              <div className="job-detail-text">
                {job.requirements}
              </div>
            </div>
          )}

          {job.skills && job.skills.length > 0 && (
            <div className="job-detail-section">
              <h2 className="h2">Required Skills</h2>
              <div className="skills-list">
                {job.skills.map((jobSkill) => (
                  <span 
                    key={jobSkill.id} 
                    className={`badge ${jobSkill.is_mandatory ? 'blue' : 'gray'}`}
                    style={{ margin: '4px 8px 4px 0', padding: '6px 12px' }}
                  >
                    {jobSkill.skill?.name}
                    {jobSkill.is_mandatory && ' *'}
                  </span>
                ))}
              </div>
              <p className="muted" style={{ fontSize: '12px', marginTop: '8px' }}>
                * Required skills
              </p>
            </div>
          )}

          {isAuthenticated && job.company && (
            <div>
              <h2 className="h2">About {job.company.name}</h2>
              <p>{job.company.description || 'A great company to work for.'}</p>
              {job.company.website && (
                <p>
                  <a href={job.company.website} target="_blank" rel="noopener noreferrer">
                    Visit company website
                  </a>
                </p>
              )}
            </div>
          )}
        </article>

        <aside className="card job-detail-sidebar">
          <div className="h2">Apply for this position</div>
          
          {!isAuthenticated ? (
            <>
              <p className="muted">Sign in to apply with your résumé and profile.</p>
              <Link className="btn" to="/login" style={{ width: '100%', justifyContent: 'center' }}>
                Sign in / Create account
              </Link>
            </>
          ) : !isCandidate ? (
            <p className="muted">Only candidates can apply for jobs.</p>
          ) : hasApplied ? (
            <div>
              <button
                type="button"
                className="btn ghost"
                style={{ width: '100%', justifyContent: 'center', marginBottom: '10px' }}
                onClick={handleToggleSave}
                disabled={saveLoading}
              >
                {saveLoading ? 'Saving...' : isSaved ? 'Saved Job' : 'Save Job'}
              </button>
              <div className="alert success">
                {applySuccess || '✅ You have already applied to this job.'}
              </div>
              <p className="muted">You can track your application status in your dashboard.</p>
              <Link to="/applications" className="btn ghost" style={{ width: '100%', justifyContent: 'center' }}>
                View My Applications
              </Link>
            </div>
          ) : (
            <div>
              <button
                type="button"
                className="btn ghost"
                style={{ width: '100%', justifyContent: 'center', marginBottom: '10px' }}
                onClick={handleToggleSave}
                disabled={saveLoading}
              >
                {saveLoading ? 'Saving...' : isSaved ? 'Saved Job' : 'Save Job'}
              </button>
              {!showApplicationForm ? (
                <>
                  {isExternal ? (
                    <>
                      <p className="muted">
                        This role is posted on{' '}
                        <strong>{(job as any).source_name || 'an external site'}</strong>.
                        Clicking apply will open it in a new tab.
                      </p>
                      <button
                        onClick={handleExternalApply}
                        className="btn"
                        style={{ width: '100%', justifyContent: 'center' }}
                      >
                        Apply on {(job as any).source_name || 'source'} ↗
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="muted">Ready to apply? Click below to submit your application.</p>
                      <button
                        onClick={handleStartApplication}
                        className="btn"
                        style={{ width: '100%', justifyContent: 'center' }}
                      >
                        Apply Now
                      </button>
                    </>
                  )}
                  {!user?.resume_filename && resumePrompt && (
                    <>
                      <div className="alert error alert--stacked">
                        {resumePrompt}
                      </div>
                      <Link to="/profile" className="btn ghost" style={{ width: '100%', justifyContent: 'center', marginTop: '8px' }}>
                        Upload Resume
                      </Link>
                    </>
                  )}
                </>
              ) : (
                <div>
                  {applyError && (
                    <div className="alert error alert--stacked">
                      {applyError}
                    </div>
                  )}
                  {applySuccess && (
                    <div className="alert success alert--stacked">
                      {applySuccess}
                    </div>
                  )}
                  <div className="form-group">
                    <label className="label">Cover Letter (Optional)</label>
                    <textarea
                      className="input textarea"
                      value={coverLetter}
                      onChange={(e) => setCoverLetter(e.target.value)}
                      placeholder="Tell the employer why you're a great fit for this role..."
                      rows={4}
                    />
                  </div>

                  <div className="form-group">
                    <label className="label">Cover Letter File (Optional)</label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleCoverLetterFileChange}
                      className="input"
                    />
                    {coverLetterFile && (
                      <div className="muted job-detail-file-meta">
                        <span>Selected: {coverLetterFile.name}</span>
                        <button
                          type="button"
                          className="btn ghost"
                          onClick={() => setCoverLetterFile(null)}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="job-detail-action-row">
                    <button
                      onClick={handleApply}
                      disabled={applying}
                      className="btn"
                      style={{ flex: 1 }}
                    >
                      {applying ? 'Submitting...' : 'Submit Application'}
                    </button>
                    <button
                      onClick={() => setShowApplicationForm(false)}
                      className="btn ghost"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {job.review_status === 'approved' && (
            <div className="job-detail-share-actions">
              <a
                href={getLinkedInShareUrl()}
                className="btn"
                style={{ width: '100%', justifyContent: 'center' }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Share on LinkedIn
              </a>
              <button
                type="button"
                className="btn ghost"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => setShowShareComposer(true)}
              >
                Share to WhatsApp, Facebook, Email
              </button>
            </div>
          )}

          <div className="job-detail-stats">
            <div className="h3">Job Details</div>
            <div className="job-detail-stats-list">
              <div className="job-detail-stats-row">
                <span className="muted">Posted:</span>
                <span>{new Date(job.created_at).toLocaleDateString()}</span>
              </div>
              
              {job.expires_at && (
                <div className="job-detail-stats-row">
                  <span className="muted">Expires:</span>
                  <span>{new Date(job.expires_at).toLocaleDateString()}</span>
                </div>
              )}
              
              <div className="job-detail-stats-row">
                <span className="muted">Applications:</span>
                <span>{job.applications_count}</span>
              </div>
              
              {hasExperienceRange(job) && (
                <div className="job-detail-stats-row">
                  <span className="muted">Experience:</span>
                  <span>{formatJobExperience(job, { verbose: true })}</span>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      {relatedJobs.length > 0 && (
        <section className="card shadow" style={{ marginTop: '24px' }}>
          <h2 className="h2" style={{ marginBottom: '12px' }}>Related jobs</h2>
          <ul className="job-detail-related" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {relatedJobs.map((r) => (
              <li
                key={r.id}
                style={{
                  padding: '10px 0',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Link
                    to={`/jobs/${jobSlug(r)}`}
                    style={{ fontWeight: 700, color: '#0f172a', textDecoration: 'none' }}
                  >
                    {r.title}
                  </Link>
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                    {r.company?.name && <span>{r.company.name}</span>}
                    {r.location && <span> · {r.location}</span>}
                    {r.job_type && <span> · {r.job_type}</span>}
                    {r.remote_allowed && <span> · Remote OK</span>}
                  </div>
                </div>
                <Link
                  to={`/jobs/${jobSlug(r)}`}
                  className="btn ghost"
                  style={{ padding: '6px 10px', fontSize: '13px' }}
                >
                  View
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {job.review_status === 'approved' && (
        <ShareComposerModal
          open={showShareComposer}
          onClose={() => setShowShareComposer(false)}
          job={job}
          jobUrl={getJobShareUrl()}
          linkedInOAuthUrl={getLinkedInShareUrl()}
        />
      )}
    </div>
  );
};

export default JobDetail;
